import React, { Fragment, RefObject } from 'react';
import { connect } from 'react-redux';
import LayoutHelper from 'infrastructure/LayoutHelper';
import { withStyles, WithStyles, Theme, createStyles } from '@material-ui/core/styles';
import { RouteLink } from '../../components/Shared/RouteLink';
import {
  Popover,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  Typography,
  Button,
  Hidden,
  CardMedia,
  Collapse,
  ButtonBase,
} from '@material-ui/core';
import routes from '../../library/Routes';
import CloseIcon from '@material-ui/icons/Close';
import { StudioPublicScheduleCalendar } from '../../components/Calendar/StudioPublicScheduleCalendar';
import { compose } from 'recompose';
import { withPublicSchedule } from '../ScheduleManagment/withPublicSchedule';
import { withLocale } from '../../hocs/withLocale';
import { SingleClassView } from './SingleClassView';
import moment from 'moment';

const styles = (theme: Theme) =>
  createStyles({
    typography: {
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    root: {
      flexGrow: 1,
      padding: theme.spacing(2),
    },
    courses: {
      paddingBottom: theme.spacing(3),
    },
    schedule: {
      maxWidth: (theme as any).width?.sectionWidth || '1200px',
      margin: 'auto',
    },
    rooms: {
      margin: theme.spacing(1),
      padding: `${theme.spacing(2)}px 0`,
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: theme.spacing(15),
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    cover: {
      width: 150,
      height: 150,
    },
    cardPopover: {
      maxWidth: '250px',
    },
    details: {
      display: 'flex',
      flexDirection: 'column',
    },
    content: {
      flex: '1 0 auto',
    },
    expand: {
      transform: 'rotate(0deg)',
      marginLeft: 'auto',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    cardAction: {
      display: 'flex',
      width: '100%',
    },
    titleDetails: {
      width: '100%',
      textAlign: 'left',
    },
    sectionHead: {
      margin: theme.spacing(0, 0, 1),
      padding: theme.spacing(0, 0, 1),
      position: 'relative',
      display: 'inline-block',
      borderBottom: '3px solid transparent',
    },
    card: {
      display: 'flex',
    },
  });

interface Room {
  _id: string;
  name: string;
  color: string;
  [key: string]: any;
}

interface Course {
  _id: string;
  title: string;
  avatar?: string;
  description?: string;
  start?: string;
  end?: string;
  allowBookEmptySlots?: boolean;
  allowBookCourse?: boolean;
  [key: string]: any;
}

interface CourseItem {
  teacherId: string[];
  [key: string]: any;
}

interface CoursesData {
  course: Course;
  items: CourseItem[];
  [key: string]: any;
}

interface Teacher {
  name: string;
  [key: string]: any;
}

interface Studio {
  domainName?: string;
  hasCourses?: boolean;
  hideSchedule?: boolean;
  [key: string]: any;
}

interface Owner {
  [key: string]: any;
}

interface CalEvent {
  id: string;
  private?: boolean;
  [key: string]: any;
}

interface CalEventData {
  calEvent: CalEvent;
  anchor: HTMLElement;
}

interface StudioScheduleViewProps extends WithStyles<typeof styles> {
  studio: Studio;
  owner: Owner;
  schedule: any;
  groupCourses: Record<string, CoursesData>;
  teachersMap: Record<string, Teacher>;
  translate: (key: string, data?: any) => string;
  user: any;
  calendarOnly?: boolean;
  courseId?: string;
  teacherId?: string;
  bookableOnly?: boolean;
}

interface StudioScheduleViewState {
  expandDescription: Record<string, boolean>;
  calEventData?: CalEventData;
  selectedCalendarEvent?: CalEvent;
}

interface ScrollRefs {
  schedule: RefObject<HTMLDivElement>;
  'schedule-calendar': RefObject<HTMLDivElement>;
  [key: string]: RefObject<HTMLDivElement>;
}

class StudioScheduleView extends React.Component<StudioScheduleViewProps, StudioScheduleViewState> {
  private scrolRefs: ScrollRefs;

  constructor(props: StudioScheduleViewProps) {
    super(props);
    this.state = { expandDescription: {} };
    this.scrolRefs = {
      schedule: React.createRef(),
      'schedule-calendar': React.createRef(),
    };
  }

  componentDidMount() {
    this.scrollToAnchor();
  }

  scrollToAnchor = () => {
    const hashParts = window.location.hash.split('#');

    if (hashParts.length > 1) {
      const hash = hashParts.slice(-1)[0];
      if (this.scrolRefs[hash] && this.scrolRefs[hash].current) {
        window.scroll({
          top: this.scrolRefs[hash].current.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  };

  renderClassOverlay = () => {
    const calEventData = this.state.calEventData;
    if (!calEventData) {
      return null;
    }

    let anchor = calEventData.anchor;
    let calEvent = calEventData.calEvent;

    return (
      <Popover
        id="event-popper"
        open={!!calEventData}
        anchorEl={anchor}
        onClose={this.hideClassPreview}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Card className={this.props.classes.cardPopover}>
          <CardActions>
            <IconButton onClick={this.hideClassPreview} className={this.props.classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </CardActions>
          <CardContent>
            <SingleClassView classEvent={calEvent} studio={this.props.studio} owner={this.props.owner} />
          </CardContent>
        </Card>
      </Popover>
    );
  };

  openClassPreview = (calEvent: CalEvent, jsEvent: React.MouseEvent<HTMLElement>) => {
    let currentSelectedEvent = this.state.selectedCalendarEvent;
    if (currentSelectedEvent && currentSelectedEvent.id === calEvent.id) {
      this.hideClassPreview();
    } else {
      if (!calEvent.private) {
        this.setState({
          calEventData: { calEvent: calEvent, anchor: jsEvent.currentTarget },
          selectedCalendarEvent: calEvent,
        });
      } else {
        this.hideClassPreview();
      }
    }
  };

  hideClassPreview = () => {
    this.setState({ calEventData: undefined, selectedCalendarEvent: undefined });
  };

  render = () => {
    if (this.props.studio) {
      return (
        <div id="schedule" ref={this.scrolRefs['schedule']}>
          {this.renderSchedule()}
        </div>
      );
    } else {
      return LayoutHelper.renderLoader();
    }
  };

  renderSchedule = () => {
    return (
      <div className={this.props.classes.schedule}>
        {!this.props.calendarOnly && !this.props.courseId && this.renderCourses()}
        {this.renderCalendar()}
      </div>
    );
  };

  renderCourses = () => {
    if (Object.keys(this.props.groupCourses).length > 0) {
      return (
        <Grid className={this.props.classes.courses} id="h1-courses" container spacing={2}>
          <Grid item>
            <Typography
              className={this.props.classes.sectionHead}
              style={{ borderColor: `orange` }}
              variant="h4"
              component="h2"
            >
              {this.props.translate('studio.courses')}
            </Typography>
          </Grid>
          {Object.keys(this.props.groupCourses)
            .sort()
            .map((key) => {
              return this.renderRepeatableClass(key, this.props.groupCourses[key]);
            })}
        </Grid>
      );
    }
    return null;
  };

  renderCalendar = () => {
    const { studio, schedule, owner } = this.props;
    if (studio.hasCourses && !studio.hideSchedule) {
      return (
        <div ref={this.scrolRefs['schedule-calendar']} id="schedule-calendar">
          <StudioPublicScheduleCalendar
            onEventClick={this.openClassPreview}
            studio={studio}
            groupCourses={this.props.groupCourses}
            owner={owner}
            courseId={this.props.courseId}
            teacherId={this.props.teacherId}
            teachersMap={this.props.teachersMap}
            schedule={schedule}
          />
          {this.renderClassOverlay()}
        </div>
      );
    }
    return null;
  };

  renderRoomLegend = (room: Room) => {
    return (
      <span style={{ borderLeft: `thick solid ${room.color}` }} key={room._id}>
        &nbsp; -&nbsp;{room.name}&nbsp;
      </span>
    );
  };

  renderRepeatableClass = (key: string, coursesData: CoursesData) => {
    const course = coursesData.course;
    if (this.props.bookableOnly && !course.allowBookEmptySlots && !course.allowBookCourse) {
      return null;
    }
    if (course.end && moment.utc(course.end).diff(moment.utc()) < 0) {
      return null;
    }
    const teacherIds: string[] = [];
    for (let i = 0; i < coursesData.items.length; i++) {
      const item = coursesData.items[i];
      for (let j = 0; j < item.teacherId.length; j++) {
        const teacherId = item.teacherId[j];
        if (!teacherIds.includes(teacherId)) {
          teacherIds.push(teacherId);
        }
      }
    }
    return (
      <Grid item xs={12} key={key}>
        <Card className={this.props.classes.card}>
          <ButtonBase
            className={this.props.classes.cardAction}
            onClick={() => {
              const expandDescription = { ...this.state.expandDescription };
              expandDescription[key] = !expandDescription[key];
              this.setState({ expandDescription: expandDescription });
            }}
          >
            {course.avatar && (
              <Hidden smDown>
                <div className={this.props.classes.details}>
                  <CardMedia className={this.props.classes.cover} image={course.avatar} title={course.title} />
                </div>
              </Hidden>
            )}
            {this.renderCourseDates(course)}
            <div className={this.props.classes.titleDetails}>
              <CardContent className={this.props.classes.content}>
                <Grid container justify="space-between" direction="row">
                  <Grid item>
                    <Typography variant="h6">{course.title}</Typography>
                    {teacherIds.map((item, index) => {
                      if (!this.state.expandDescription[key]) {
                        if (index === 2) {
                          return '...';
                        }
                        if (index > 2) {
                          return null;
                        }
                      }
                      let teacher = this.props.teachersMap[item];
                      if (teacher) {
                        return (
                          <Typography key={index} variant="subtitle1" color="textSecondary">
                            {teacher.name}
                          </Typography>
                        );
                      }
                      return null;
                    })}
                  </Grid>
                  {this.renderBookEmptySpots(course)}
                  {this.renderBookCourse(course)}
                </Grid>
              </CardContent>
            </div>
          </ButtonBase>
          <Collapse in={this.state.expandDescription[key]}>
            <CardContent>{this.renderCourseDescription(course)}</CardContent>
          </Collapse>
        </Card>
      </Grid>
    );
  };

  renderCourseDescription = (course: Course) => {
    if (course.description) {
      return <Typography component="div" align="left" dangerouslySetInnerHTML={{ __html: course.description }} />;
    } else {
      return (
        <Typography component="div" align="left">
          {this.props.translate('studio.noCourseDescription')}
        </Typography>
      );
    }
  };

  renderBookCourse = (course: Course) => {
    if (course.allowBookCourse && (!course.end || moment.utc(course.end).diff(moment.utc()) > 0)) {
      return (
        <Grid item>
          <Button
            color="primary"
            variant="outlined"
            component={RouteLink}
            to={routes.courseBooking({
              domainName: this.props.studio.domainName,
              courseId: course._id,
            })}
            target="_blank"
          >
            {this.props.translate('studio.course.book')}
          </Button>
        </Grid>
      );
    }
    return null;
  };

  renderBookEmptySpots = (course: Course) => {
    if (course.allowBookEmptySlots) {
      return (
        <Fragment>
          <Hidden mdUp>
            <Grid item xs={12}>
              <Button
                color="primary"
                variant="outlined"
                component={RouteLink}
                to={routes.groupBooking({
                  domainName: this.props.studio.domainName,
                  courseId: course._id,
                })}
                target="_blank"
              >
                {this.props.translate('studio.course.book')}
              </Button>
            </Grid>
          </Hidden>
          <Hidden smDown>
            <Grid item>
              <Button
                color="primary"
                variant="outlined"
                component={RouteLink}
                to={routes.groupBooking({
                  domainName: this.props.studio.domainName,
                  courseId: course._id,
                })}
                target="_blank"
              >
                {this.props.translate('studio.course.book')}
              </Button>
            </Grid>
          </Hidden>
        </Fragment>
      );
    }
    return null;
  };

  renderCourseDates = (course: Course) => {
    if (course.start && course.end) {
      let start = moment.utc(course.start);
      let end = moment.utc(course.end);
      return (
        <Fragment>
          <div className={this.props.classes.details}>
            <CardContent className={this.props.classes.content}>
              <Typography component="h5" variant="h5" align="center">
                {start.format('DD')}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {start.format('MMM')}
              </Typography>
              <Typography variant="subtitle2" component="span" noWrap color="textSecondary" align="center">
                {end.format('DD MMM')}
              </Typography>
            </CardContent>
          </div>
        </Fragment>
      );
    }
    return null;
  };
}

export default compose<React.ComponentType<Omit<StudioScheduleViewProps, 'classes'>>>(
  withLocale,
  withPublicSchedule,
  connect(({ user }: { user: any }) => ({ user })),
  withStyles(styles)
)(StudioScheduleView);
